import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { partSecAttributesApi, PartSecAttribute, PartSecAttributeSearchParams } from '../../services/partSecAttributesApi';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setStatistics, setBulkOperationLoading, setSuccessMessage,
  FETCH_PART_SEC_ATTRIBUTES, FETCH_PART_SEC_ATTRIBUTE_BY_ID, CREATE_PART_SEC_ATTRIBUTE, UPDATE_PART_SEC_ATTRIBUTE, DELETE_PART_SEC_ATTRIBUTE,
  SEARCH_PART_SEC_ATTRIBUTES, FETCH_PART_SEC_ATTRIBUTES_STATS, BULK_PART_SEC_ATTRIBUTES_ACTIVATE, BULK_PART_SEC_ATTRIBUTES_DEACTIVATE,
  BULK_PART_SEC_ATTRIBUTES_DELETE, EXPORT_PART_SEC_ATTRIBUTES_CSV,
} from '../reducers/partSecAttributesSlice';

function* fetchSaga(action: PayloadAction<PartSecAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.partSecAttributes;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(partSecAttributesApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to fetch Part Secondary Attributes')); }
  finally { yield put(setLoading(false)); }
}

function* fetchByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const item = yield call(partSecAttributesApi.getById, action.payload); yield put(setSelectedItem(item)); }
  catch (e: any) { yield put(setError(e.message || 'Failed to fetch Part Secondary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* createSaga(action: PayloadAction<Omit<PartSecAttribute, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const errors = yield call(partSecAttributesApi.validation.validate, action.payload);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const created = yield call(partSecAttributesApi.create, action.payload);
    yield put(addItem(created)); yield put(setSuccessMessage('Created successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to create Part Secondary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* updateSaga(action: PayloadAction<{ id: number; item: Partial<PartSecAttribute> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const { id, item } = action.payload;
    const errors = yield call(partSecAttributesApi.validation.validate, item);
    if (errors.length) { yield put(setValidationErrors(errors)); return; }
    const updated = yield call(partSecAttributesApi.update, id, item);
    yield put(updateItem(updated)); yield put(setSuccessMessage('Updated successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to update Part Secondary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* deleteSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partSecAttributesApi.delete, action.payload);
    if (resp.success) { yield put(removeItem(action.payload)); yield put(setSuccessMessage('Deleted successfully')); }
    else { yield put(setError(resp.message || 'Failed to delete')); }
  } catch (e: any) { yield put(setError(e.message || 'Failed to delete Part Secondary Attribute')); }
  finally { yield put(setLoading(false)); }
}

function* searchSaga(action: PayloadAction<PartSecAttributeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const resp = yield call(partSecAttributesApi.search, action.payload);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to search Part Secondary Attributes')); }
  finally { yield put(setLoading(false)); }
}

function* statsSaga(): Generator<any, void, any> {
  try { const stats = yield call(partSecAttributesApi.getStatistics); yield put(setStatistics(stats)); } catch (_) {}
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partSecAttributesApi.bulkActivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_PART_SEC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to activate')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partSecAttributesApi.bulkDeactivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_PART_SEC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to deactivate')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkOperationLoading(true)); const resp = yield call(partSecAttributesApi.bulkDelete, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_PART_SEC_ATTRIBUTES }); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  finally { yield put(setBulkOperationLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<PartSecAttributeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.partSecAttributes.searchParams, ...action.payload };
    const blob = yield call(partSecAttributesApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `part_sec_attributes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to export Part Secondary Attributes')); }
  finally { yield put(setLoading(false)); }
}

export function* partSecAttributesSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_PART_SEC_ATTRIBUTES, fetchSaga);
  yield takeEvery(FETCH_PART_SEC_ATTRIBUTE_BY_ID, fetchByIdSaga);
  yield takeEvery(CREATE_PART_SEC_ATTRIBUTE, createSaga);
  yield takeEvery(UPDATE_PART_SEC_ATTRIBUTE, updateSaga);
  yield takeEvery(DELETE_PART_SEC_ATTRIBUTE, deleteSaga);
  yield takeEvery(SEARCH_PART_SEC_ATTRIBUTES, searchSaga);
  yield takeEvery(FETCH_PART_SEC_ATTRIBUTES_STATS, statsSaga);
  yield takeEvery(BULK_PART_SEC_ATTRIBUTES_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_PART_SEC_ATTRIBUTES_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_PART_SEC_ATTRIBUTES_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_PART_SEC_ATTRIBUTES_CSV, exportCsvSaga);
} 